import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Modal } from '../components/Modal';
import { useToast } from '../context/ToastContext';
import { useConfirmDialog } from '../context/ConfirmDialogContext';
import DataTable from '../components/DataTable';
import { formatDate } from '../utils/format';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Labels are i18n keys, translated at render time. durationDays: null = unlimited.
const LICENSE_TYPES = [
  { value: 'saas_monthly', icon: '📅', labelKey: 'licenses.company.typeLabel.saas_monthly', durationDays: 30 },
  { value: 'saas_yearly', icon: '📆', labelKey: 'licenses.company.typeLabel.saas_yearly', durationDays: 365 },
  { value: 'saas_lifetime', icon: '♾️', labelKey: 'licenses.company.typeLabel.saas_lifetime', durationDays: null },
  { value: 'on_premise', icon: '💻', labelKey: 'licenses.company.typeLabel.on_premise', durationDays: null }
];

// Licensable module catalog. The codes and their order gate licensing (see
// CLAUDE.md "Adding a licensable module"); nameKey is translated at render.
const MODULE_OPTIONS = [
  { code: 'service', nameKey: 'shared.moduleNames.service', icon: '🔧' },
  { code: 'hr', nameKey: 'shared.moduleNames.hr', icon: '👥' },
  { code: 'inventory', nameKey: 'shared.moduleNames.inventory', icon: '📦' },
  { code: 'sales', nameKey: 'shared.moduleNames.sales', icon: '💰' },
  { code: 'purchasing', nameKey: 'shared.moduleNames.purchasing', icon: '🛒' },
  { code: 'tender', nameKey: 'shared.moduleNames.tender', icon: '⚖️' },
  { code: 'accounting', nameKey: 'shared.moduleNames.accounting', icon: '📊' },
  { code: 'finance', nameKey: 'shared.moduleNames.finance', icon: '💳' },
  { code: 'crm', nameKey: 'shared.moduleNames.crm', icon: '🤝' },
  { code: 'production', nameKey: 'shared.moduleNames.production', icon: '🏭' },
  { code: 'assets', nameKey: 'shared.moduleNames.assets', icon: '🏢' },
  { code: 'tasks', nameKey: 'shared.moduleNames.tasks', icon: '✅' },
  { code: 'office', nameKey: 'shared.moduleNames.office', icon: '📄' },
  { code: 'reservations', nameKey: 'shared.moduleNames.reservations', icon: '🗓️' },
  { code: 'insurance', nameKey: 'shared.moduleNames.insurance', icon: '🛡️' },
  { code: 'education', nameKey: 'shared.moduleNames.education', icon: '🎓' },
  { code: 'membership', nameKey: 'shared.moduleNames.membership', icon: '🎟️' },
  { code: 'ledger', nameKey: 'shared.moduleNames.ledger', icon: '📒' },
  { code: 'projects', nameKey: 'shared.moduleNames.projects', icon: '🏗️' }
];

function CompanyLicensesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirmDialog();
  const [companies, setCompanies] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    license_type: 'saas_monthly',
    modules: ['service'],
    user_limit: 10,
    auto_renew: false
  });

  useEffect(() => {
    fetchCompanies();
    fetchLicenses();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchLicenses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/licenses`);
      // Filter only main licenses (with license_key)
      const mainLicenses = response.data.filter(lic => lic.license_key);
      setLicenses(mainLicenses);
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingLicense) {
        await axios.put(`${API_URL}/api/licenses/${editingLicense.id}`, formData);
      } else {
        await axios.post(`${API_URL}/api/licenses`, formData);
      }
      
      setShowModal(false);
      setEditingLicense(null);
      fetchLicenses();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('licenses.saveFailed'));
    }
  };

  const licenseOwner = (license) => license.company_name || getCompanyName(license.company_id);

  const handleRenew = async (license) => {
    const licenseId = license.id;
    const confirmed = await confirm({
      title: t('sharedui.adminConfirm.renewLicenseTitle'),
      message: t('sharedui.adminConfirm.renewLicense', { name: licenseOwner(license) }),
      confirmText: t('sharedui.adminConfirm.renewButton'),
      cancelText: t('sharedui.adminConfirm.cancelButton'),
      type: 'info',
    });
    if (!confirmed) return;

    try {
      await axios.post(`${API_URL}/api/licenses/${licenseId}/renew`);
      fetchLicenses();
      toast.success(t('licenses.company.renewed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('licenses.company.renewFailed'));
    }
  };

  const handleStatusToggle = async (license) => {
    const licenseId = license.id;
    const newStatus = license.status === 'active' ? 'suspended' : 'active';
    // Suspending cuts the company off its licensed modules: confirm first.
    if (newStatus === 'suspended') {
      const confirmed = await confirm({
        title: t('sharedui.adminConfirm.suspendLicenseTitle'),
        message: t('sharedui.adminConfirm.suspendLicense', { name: licenseOwner(license) }),
        confirmText: t('sharedui.adminConfirm.suspendButton'),
        cancelText: t('sharedui.adminConfirm.cancelButton'),
        type: 'danger',
      });
      if (!confirmed) return;
    }
    try {
      await axios.put(`${API_URL}/api/licenses/${licenseId}`, { status: newStatus });
      fetchLicenses();
    } catch (error) {
      toast.error(t('licenses.company.statusChangeFailed'));
    }
  };

  const handleDelete = async (license) => {
    const licenseId = license.id;
    const confirmed = await confirm({
      title: t('sharedui.adminConfirm.deleteLicenseTitle'),
      message: t('sharedui.adminConfirm.deleteLicenseWithModules', { name: licenseOwner(license) }),
      confirmText: t('sharedui.adminConfirm.deleteButton'),
      cancelText: t('sharedui.adminConfirm.cancelButton'),
      type: 'danger',
    });
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/api/licenses/${licenseId}`);
      fetchLicenses();
    } catch (error) {
      toast.error(t('licenses.deleteFailed'));
    }
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      company_id: license.company_id,
      license_type: license.license_type,
      modules: license.modules || [],
      user_limit: license.user_limit,
      auto_renew: license.auto_renew || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      license_type: 'saas_monthly',
      modules: ['service'],
      user_limit: 10,
      auto_renew: false
    });
  };

  const toggleModule = (moduleCode) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleCode)
        ? prev.modules.filter(m => m !== moduleCode)
        : [...prev.modules, moduleCode]
    }));
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : t('licenses.company.unknownCompany');
  };

  const getLicenseTypeName = (type) => {
    const licenseType = LICENSE_TYPES.find(lt => lt.value === type);
    return licenseType ? `${licenseType.icon} ${t(licenseType.labelKey)}` : type;
  };

  const isExpired = (endDate) => {
    if (!endDate) return false; // Lifetime
    return new Date(endDate) < new Date();
  };

  const isExpiringSoon = (endDate) => {
    if (!endDate) return false;
    const daysLeft = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 && daysLeft <= 30;
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return t('licenses.company.unlimited');
    const daysLeft = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return t('licenses.company.expiredLower');
    if (daysLeft === 0) return t('licenses.company.expiresToday');
    return t('licenses.company.daysLeft', { count: daysLeft });
  };

  const licenseStatusLabel = (license) => (
    isExpired(license.end_date) ? t('licenses.status.expired')
      : license.status === 'active' ? t('licenses.status.active') : t('licenses.status.suspended')
  );

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:page-title text-3xl mb-2">📜 {t('licenses.title')}</h1>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{t('licenses.company.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setEditingLicense(null);
            resetForm();
            setShowModal(true);
          }}
          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          + {t('licenses.company.newLicense')}
        </button>
      </div>

      <DataTable
        id="company-licenses"
        rows={licenses}
        loading={loading}
        exportName="firma-lisanslari"
        defaultSort={{ key: 'company_name', order: 'asc' }}
        emptyText={t('licenses.company.empty')}
        columns={[
          { key: 'company_name', header: t('licenses.columns.company'), sortable: true,
            render: (license) => <span className="font-semibold text-gray-900 dark:text-gray-100">{license.company_name}</span> },
          { key: 'license_type', header: t('licenses.columns.type'), sortable: true,
            value: (license) => getLicenseTypeName(license.license_type),
            render: (license) => <span className="whitespace-nowrap">{getLicenseTypeName(license.license_type)}</span> },
          { key: 'status', header: t('common.status'), sortable: true,
            value: (license) => licenseStatusLabel(license),
            render: (license) => (
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                  isExpired(license.end_date)
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    : license.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                }`}
              >
                {licenseStatusLabel(license)}
              </span>
            ) },
          { key: 'license_key', header: t('licenses.licenseKey'), sortable: true,
            render: (license) => <span className="font-mono text-xs break-all">{license.license_key}</span> },
          { key: 'modules', header: t('licenses.modules'),
            value: (license) => (license.modules || []).map((mod) => {
              const moduleInfo = MODULE_OPTIONS.find(m => m.code === mod);
              return moduleInfo ? t(moduleInfo.nameKey) : mod;
            }).join(', '),
            render: (license) => (
              <div className="flex flex-wrap gap-1">
                {license.modules?.map(mod => {
                  const moduleInfo = MODULE_OPTIONS.find(m => m.code === mod);
                  return (
                    <span key={mod} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                      {moduleInfo ? `${moduleInfo.icon} ${t(moduleInfo.nameKey)}` : mod}
                    </span>
                  );
                })}
              </div>
            ) },
          { key: 'user_limit', header: t('licenses.columns.userLimit'), sortable: true, align: 'right',
            render: (license) => <span className="font-medium">{license.user_limit}</span> },
          { key: 'remaining', header: t('licenses.columns.remaining'),
            value: (license) => (license.end_date ? getDaysLeft(license.end_date) : t('licenses.company.unlimited')),
            render: (license) => (license.end_date ? (
              <span className={`font-medium whitespace-nowrap ${
                isExpired(license.end_date) ? 'text-red-600' :
                isExpiringSoon(license.end_date) ? 'text-orange-600' :
                'text-green-600'
              }`}>
                {getDaysLeft(license.end_date)}
              </span>
            ) : (
              <span className="font-medium text-green-600 whitespace-nowrap">{t('licenses.company.unlimited')}</span>
            )) },
          { key: 'end_date', header: t('licenses.columns.endDate'), sortable: true, defaultHidden: true,
            value: (license) => (license.end_date ? formatDate(license.end_date) : ''),
            render: (license) => (license.end_date ? formatDate(license.end_date) : '—') },
          { key: 'auto_renew', header: t('licenses.columns.autoRenew'), align: 'center',
            value: (license) => Boolean(license.auto_renew),
            render: (license) => (license.auto_renew ? (
              <span className="inline-flex text-blue-600" title={t('licenses.company.autoRenewActive')}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-label={t('licenses.company.autoRenewActive')}>
                  <path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
                </svg>
              </span>
            ) : null) },
        ]}
        rowActions={(license) => (
          <span className="inline-flex flex-wrap justify-end gap-2">
            {license.end_date && !isExpired(license.end_date) && isExpiringSoon(license.end_date) && (
              <button
                onClick={() => handleRenew(license)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-3 rounded-lg transition text-xs"
              >
                🔄 {t('licenses.company.renew')}
              </button>
            )}
            {isExpired(license.end_date) && (
              <button
                onClick={() => handleRenew(license)}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-1.5 px-3 rounded-lg transition text-xs"
              >
                ✅ {t('licenses.company.renew')}
              </button>
            )}
            <button
              onClick={() => handleEdit(license)}
              className="bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium py-1.5 px-3 rounded-lg transition text-xs"
            >
              {t('common.edit')}
            </button>
            <button
              onClick={() => handleStatusToggle(license)}
              className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 font-medium py-1.5 px-3 rounded-lg transition text-xs"
            >
              {license.status === 'active' ? t('licenses.company.suspend') : t('licenses.company.activate')}
            </button>
            <button
              onClick={() => handleDelete(license)}
              className="bg-red-50 hover:bg-red-100 text-red-700 font-medium py-1.5 px-3 rounded-lg transition text-xs"
            >
              {t('common.delete')}
            </button>
          </span>
        )}
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingLicense(null);
        }}
        title={editingLicense ? t('licenses.editLicense') : t('licenses.createLicense')}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">
              {t('licenses.company.companyRequired')}
            </label>
            <select
              value={formData.company_id}
              onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
              required
              disabled={editingLicense}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm disabled:bg-gray-100 dark:bg-surface-800"
            >
              <option value="">{t('licenses.company.selectCompany')}</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              {t('licenses.licenseTypeRequired')}
            </label>
            <select
              value={formData.license_type}
              onChange={(e) => setFormData({ ...formData, license_type: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
            >
              {LICENSE_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {t(type.labelKey)} ({type.durationDays ? t('licenses.company.durationDays', { count: type.durationDays }) : t('licenses.company.unlimited')})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">{t('licenses.company.modulesRequired')}</label>
            <div className="grid grid-cols-2 gap-3">
              {MODULE_OPTIONS.map(module => (
                <label
                  key={module.code}
                  className="flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-surface-900 transition"
                  style={{
                    borderColor: formData.modules.includes(module.code) ? '#3b82f6' : '#e5e7eb'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.modules.includes(module.code)}
                    onChange={() => toggleModule(module.code)}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">
                    {module.icon} {t(module.nameKey)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="form-label">{t('licenses.userLimitRequired')}</label>
            <input
              type="number"
              value={formData.user_limit}
              onChange={(e) => setFormData({ ...formData, user_limit: parseInt(e.target.value) })}
              required
              min="1"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
            />
          </div>

          {(formData.license_type === 'saas_monthly' || formData.license_type === 'saas_yearly') && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_renew}
                  onChange={(e) => setFormData({ ...formData, auto_renew: e.target.checked })}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('licenses.company.autoRenewActive')}
                </span>
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingLicense(null);
              }}
              className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold py-3 px-4 rounded-lg transition"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={formData.modules.length === 0}
              className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editingLicense ? t('common.update') : t('common.create')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CompanyLicensesPage;